/**
 * SiteAIChat — chat panel that lets users iteratively build/edit their
 * website by talking to AI. Reachable from the Site editor's "Chat with AI
 * to build" button.
 *
 * Flow per turn:
 *  1. User types ("Make the hero say X" / "Add a Pricing section" / "Switch to bold theme")
 *  2. Frontend POSTs instruction + recent history to /api/sites/ai-edit
 *  3. Backend returns { summary, patch }
 *  4. Frontend applies the patch to local site state (preview-only), shows
 *     the assistant's summary in the chat, and offers an "Apply & Save"
 *     button. Until the user clicks Apply, nothing is persisted server-side.
 *
 * This is intentionally a SEPARATE component from the AI Studio /ai-hub
 * route — the website editor is structured (not free-form Markdown chat),
 * and using its own component keeps the patch-apply loop tight.
 */
import { useEffect, useRef, useState } from 'react';
import { Sparkles, Send, X, Check, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { apiFetch } from '../../fetchInterceptor';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SAMPLE_PROMPTS = [
  'Make the hero headline shorter and more punchy',
  'Add a testimonial from a happy client named Jess',
  'Switch to the bold theme with a dark purple color',
  'Add a new service: Quarterly tax planning, $1,200/quarter',
  'Make the about section sound more friendly',
];

// Deep-merge patch onto the current site (arrays are replaced wholesale).
function applyPatch(site, patch) {
  if (!patch || typeof patch !== 'object') return site;
  const out = { ...site };
  for (const [k, v] of Object.entries(patch)) {
    if (k === 'sections' && typeof v === 'object') {
      out.sections = { ...site.sections };
      for (const [sk, sv] of Object.entries(v)) {
        out.sections[sk] = { ...site.sections[sk], ...sv };
      }
    } else if (k === 'seo' && typeof v === 'object') {
      out.seo = { ...site.seo, ...v };
    } else {
      out[k] = v;
    }
  }
  return out;
}

export default function SiteAIChat({ workspaceId, site, onApply, onClose }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I can edit your website for you — change copy, add sections, swap themes, even redesign the whole thing. Just tell me what you want.",
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingSite, setPendingSite] = useState(null); // preview-only patched site
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(() => localStorage.getItem('siteAiModel') || 'anthropic/claude-sonnet-4.5');
  const scrollRef = useRef(null);

  // Fetch the model catalog once
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/sites/ai-models`);
        if (r.ok) {
          const j = await r.json();
          setModels(j.models || []);
          if (!localStorage.getItem('siteAiModel') && j.default) setModel(j.default);
        }
      } catch { /* keep default */ }
    })();
  }, []);

  // Persist model choice
  useEffect(() => { localStorage.setItem('siteAiModel', model); }, [model]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async (text) => {
    const instruction = (text || input).trim();
    if (!instruction || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: instruction }]);
    setSending(true);
    try {
      const r = await apiFetch(`${API_URL}/api/sites/ai-edit?workspace_id=${workspaceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instruction,
          model,
          history: messages.slice(-6).map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setMessages((m) => [...m, { role: 'assistant', content: j.detail || 'I hit an error — try rephrasing.' }]);
        return;
      }
      const j = await r.json();
      setMessages((m) => [...m, { role: 'assistant', content: j.summary, patch: j.patch }]);
      // Apply the patch to the local preview-only site
      if (j.patch && Object.keys(j.patch).length > 0) {
        setPendingSite((prev) => applyPatch(prev || site, j.patch));
      }
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Network hiccup — please try again.' }]);
    } finally {
      setSending(false);
    }
  };

  const handleApply = async () => {
    if (!pendingSite) return;
    try {
      await onApply(pendingSite);
      toast.success('Saved!');
      setPendingSite(null);
      setMessages((m) => [...m, { role: 'assistant', content: '✓ Changes saved to your live editor. Anything else?' }]);
    } catch {
      toast.error('Save failed');
    }
  };

  const handleDiscard = () => {
    setPendingSite(null);
    setMessages((m) => [...m, { role: 'assistant', content: 'OK — pending changes discarded. What else?' }]);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 sm:bottom-4 sm:right-4 sm:left-auto sm:w-[480px] z-40 max-h-[80vh] flex flex-col rounded-t-2xl sm:rounded-2xl border bg-white dark:bg-zinc-950 shadow-2xl"
      data-testid="site-ai-chat"
    >
      <header className="px-4 py-3 border-b bg-gradient-to-r from-violet-50 to-blue-50 dark:from-violet-950/30 dark:to-blue-950/30 rounded-t-2xl">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-semibold text-sm">Website AI</p>
              <p className="text-[10px] text-muted-foreground">Edit your site by chatting</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-accent rounded-md" data-testid="site-ai-chat-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {models.length > 0 && (
          <div className="mt-2.5 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">Model</span>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={sending}
              className="flex-1 h-7 px-2 text-xs rounded-md border bg-white dark:bg-zinc-900 focus:outline-none focus:ring-1 focus:ring-violet-500"
              data-testid="site-ai-model-picker"
            >
              {models.map((m) => (
                <option key={m.slug} value={m.slug}>
                  {m.label} — {m.description}
                </option>
              ))}
            </select>
          </div>
        )}
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[200px] max-h-[50vh]">
        {messages.map((m, i) => (
          <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div
              className={cn(
                'max-w-[85%] rounded-2xl px-3 py-2 text-sm',
                m.role === 'user'
                  ? 'bg-violet-600 text-white rounded-br-sm'
                  : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded-bl-sm',
              )}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-zinc-100 dark:bg-zinc-800 rounded-2xl px-3 py-2 text-sm text-muted-foreground inline-flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Thinking…
            </div>
          </div>
        )}
        {/* Sample prompts shown when conversation is empty */}
        {messages.length <= 1 && (
          <div className="space-y-1.5 pt-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Try asking</p>
            {SAMPLE_PROMPTS.map((p) => (
              <button
                key={p}
                onClick={() => handleSend(p)}
                className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-900 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                data-testid="site-ai-sample-prompt"
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pending-changes apply bar */}
      {pendingSite && (
        <div className="px-4 py-2.5 border-t border-violet-200 bg-violet-50 dark:bg-violet-950/30 flex items-center gap-2" data-testid="site-ai-pending">
          <Sparkles className="w-4 h-4 text-violet-600 flex-shrink-0" />
          <p className="text-xs text-violet-900 dark:text-violet-200 flex-1">
            Preview ready in the editor on the left.
          </p>
          <Button size="sm" variant="ghost" onClick={handleDiscard} className="h-7 px-2 text-xs">
            <RotateCcw className="w-3 h-3 mr-1" /> Discard
          </Button>
          <Button size="sm" onClick={handleApply} className="h-7 px-2.5 text-xs bg-violet-600 hover:bg-violet-700 text-white" data-testid="site-ai-apply-changes">
            <Check className="w-3 h-3 mr-1" /> Apply & save
          </Button>
        </div>
      )}

      <div className="p-3 border-t flex gap-2">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Make the hero shorter…  (Enter to send, Shift+Enter for newline)"
          rows={2}
          className="resize-none text-sm"
          data-testid="site-ai-input"
        />
        <Button
          onClick={() => handleSend()}
          disabled={sending || !input.trim()}
          className="bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="site-ai-send"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
